/* eslint-disable tailwindcss/no-custom-classname */
import { formatEther, parseEther } from 'ethers'
import { FunctionComponent, useEffect, useState } from 'react'

import { Box, useToast } from '@chakra-ui/react'
import { useChainModal } from '@rainbow-me/rainbowkit'
import { avalanche, avalancheFuji } from 'viem/chains'
import { useAccount, useBalance, useWaitForTransactionReceipt, useWatchAsset } from 'wagmi'

import { RewardForm } from './RewardForm'
import { StakeForm } from './StakeForm'
import { Statistics } from './Statistics'
import GoGoPoolNotice from '../Vault/GoGoPoolNotice'
import ApproveAVAXButton from '../Wizard/components/ApproveAVAXButton'
import ApproveButton from '../Wizard/components/ApproveButton'

import { Address } from '@/common/components/Address'
import ConnectButton from '@/common/components/ConnectButton'
import { InfoCircleIcon } from '@/common/components/CustomIcon'
import { SwapIcon } from '@/common/components/CustomIcon/SwapIcon'
import { Tooltip } from '@/common/components/Tooltip'
import { useValueTokens } from '@/contexts/VaultTokensContext'
import { Button, FOCUS, Panel } from '@/design-system/ui'
import { useGGPVaultAllowance } from '@/hooks/allowance'
import useDeposit from '@/hooks/deposit'
import useLiquidStakingData from '@/hooks/liquidStakingData'
import useRedeem from '@/hooks/redeem'
import { useMaxDeposit, useMaxRedeem } from '@/hooks/useAvaxBalanceOfggAvax'
import { useCoinGeckoPrices } from '@/hooks/useCoinGeckoPrices'
import {
  useAPYinBasis,
  useUnderlyingBalance,
  useVaultCap,
  useVaultRatio,
  useVaultTotalAssets,
} from '@/hooks/useGetVaultData'
import { VaultTokenType } from '@/types/vaultTokensTypes'
import { WEI_VALUE } from '@/utils/consts'
import { formatEtherFixed } from '@/utils/formatEtherFixed'
import { displayBN, formatNumber } from '@/utils/numberFormatter'

const generateStatistics = (
  currentToken: VaultTokenType,
  apy: number | string,
  exchangeRate: bigint,
  stakedAmount: bigint,
  stakers: bigint | string,
  vaultCap: bigint | string,
  tokenAddress?: string | null | undefined,
  coingeckoPrices?: { [key: string]: { usd: number } },
) => {
  const USDValue = (value: bigint | number | string) => {
    const formatValue = Number(value)

    if (!coingeckoPrices) {
      return 'Loading...'
    }

    let price

    switch (currentToken.value) {
      case 'ggp':
        price = coingeckoPrices['gogopool'].usd
        break
      case 'avax':
        price = coingeckoPrices['avalanche-2'].usd
        break
      default:
        return 'Loading...'
    }

    return formatNumber(formatValue * price)
  }

  return [
    {
      label: (
        <>
          Token Address
          <Tooltip content="The address of the xGGP token" placement="right">
            <Box as="span">
              <InfoCircleIcon className="ml-1" fill="grey.600" />
            </Box>
          </Tooltip>
        </>
      ),
      value: tokenAddress ? (
        <Box alignItems="center" display="flex">
          <Address copyable fontWeight="black">
            {tokenAddress}
          </Address>
          <a
            href={`https://snowtrace.io/token/${tokenAddress}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Box as="span" className="ml-2" fontWeight="bold">
              →
            </Box>
          </a>
        </Box>
      ) : (
        'Loading...'
      ),
    },

    {
      label: (
        <>
          Annual Percentage Yield
          <Tooltip
            content="Estimated Percentage reward you get per year on your staked GGP."
            placement="right"
          >
            <Box as="span">
              <InfoCircleIcon className="ml-1" fill="grey.600" />
            </Box>
          </Tooltip>
        </>
      ),
      value: typeof apy === 'string' ? apy : `~${apy.toFixed(2)}%`,
    },
    {
      label: (
        <>
          Exchange Rate
          <Tooltip
            content={`Rate of exchange between x${currentToken.displayName} and ${currentToken.displayName}.`}
            placement="right"
          >
            <Box as="span">
              <InfoCircleIcon className="ml-1" fill="grey.600" />
            </Box>
          </Tooltip>
        </>
      ),
      value: `1 x${currentToken.displayName} = ${Number(formatEther(exchangeRate)).toFixed(6)} ${currentToken.displayName}`,
    },
    // {
    //   label: <># of Stakers</>,
    //   value: typeof stakers === 'string' ? stakers : stakers.toLocaleString(),
    // },
    {
      label: <>Total {currentToken.displayName} Staked</>,
      value: `${formatNumber(formatEtherFixed(stakedAmount))} ${currentToken.displayName} ($${USDValue(formatEtherFixed(stakedAmount))})`,
    },
    {
      label: <>{currentToken.displayName} Vault Cap</>,
      value: `${formatNumber(formatEtherFixed(vaultCap))} ${currentToken.displayName} ($${USDValue(formatEtherFixed(vaultCap))})`,
    },
    // {
    //   label: (
    //     <>
    //       Reward Period
    //       <Tooltip content="The waiting period before rewards are gained" placement="right">
    //         <Box as="span">
    //           <InfoCircleIcon className="ml-1" fill="grey.600" />
    //         </Box>
    //       </Tooltip>
    //     </>
    //   ),
    //   value: ms(rewardPeriod, { long: true }),
    // },
  ]
}

export const LiquidStaking: FunctionComponent = () => {
  const toast = useToast()
  const { watchAsset } = useWatchAsset()

  const { openChainModal } = useChainModal()

  const { chain } = useAccount()

  const { currentToken } = useValueTokens()

  const GGPAddress = currentToken.tokenAddresses[0][chain?.id || avalanche.id] as `0x${string}`
  const xGGPAddress = currentToken.tokenAddresses[1][chain?.id || avalanche.id] as `0x${string}`

  // GoGoPool is winding down: the GGP vault is withdraw-only, so it opens in the
  // redeem direction (xGGP -> GGP) and the deposit direction is not reachable.
  const isWithdrawOnly = currentToken.value === 'ggp'
  const [swapDirection, setSwapDirection] = useState(isWithdrawOnly) // false = deposit, true = redeem
  const [amount, setAmount] = useState<bigint>(0n) // stake value
  const [reward, setReward] = useState<bigint>(0n) // reward value

  const { address: account, isConnected } = useAccount()
  const { data: maxDeposit } = useMaxDeposit()
  const { data: maxRedeem } = useMaxRedeem()
  const { data: vaultCap } = useVaultCap()

  const { isLoading: isLoadingStats, xGGPExchangeRate } = useLiquidStakingData()

  const { prices: coingeckoPrices } = useCoinGeckoPrices()

  // // AVAX balance
  // const { data: balance, isLoading: isBalanceLoading } = useBalance({
  //   watch: true,
  //   address: account,
  // })

  // GGP Balance
  const { data: balance, isLoading: isBalanceLoading } = useBalance({
    address: account,
    token: GGPAddress,
    query: {
      refetchInterval: 5000,
    },
  })

  // xGGP balance
  const { data: ggAVAXBalance } = useBalance({
    address: account,
    token: xGGPAddress,
    query: {
      refetchInterval: 5000,
    },
  })

  // deposit the AVAX
  const {
    data: depositData,
    isError: isDepositError,
    isLoading: isDepositLoading,
    refetch: refetchDeposit,
    write: deposit,
    hash: depositHash,
  } = useDeposit(amount)

  // redeem ggAVAX
  const {
    data: redeemData,
    isError: isRedeemError,
    isLoading: isRedeemLoading,
    write: redeem,
    hash: redeemHash,
  } = useRedeem(amount)

  const { status: redeemStatus, isLoading: isRedeemWaiting } = useWaitForTransactionReceipt({
    hash: redeemHash,
  })
  const { status: depositStatus, isLoading: isDepositWaiting } = useWaitForTransactionReceipt({
    hash: depositHash,
  })
  const { data: underlyingBalnce } = useUnderlyingBalance()

  const isLoading =
    isBalanceLoading ||
    isDepositWaiting ||
    isRedeemWaiting ||
    isLoadingStats ||
    isDepositLoading ||
    isRedeemLoading
  const { data: vaultTotalAssets } = useVaultTotalAssets()
  const { data: ratio } = useVaultRatio()
  const { data: apy } = useAPYinBasis()
  const apyInString = (apy ? Number(apy) / 100 : 0).toString() + ' %'
  const statisticData = generateStatistics(
    currentToken,
    apyInString,
    ratio || 0n,
    vaultTotalAssets || 0n,
    0n,
    vaultCap || 0n,
    xGGPAddress,
    coingeckoPrices,
  )

  const handleSwap = () => {
    const temporaryAmount = amount
    const temporaryReward = reward
    setSwapDirection(!swapDirection)
    setAmount(temporaryReward)
    setReward(temporaryAmount)
  }

  const clearAmount = () => {
    setAmount(0n)
    setReward(0n)
  }

  useEffect(() => {
    if (depositStatus === 'success') {
      toast({
        position: 'top',
        title: 'Success',
        status: 'success',
        duration: 8000,
        description: <span>Deposited!</span>,
      })
      setAmount(parseEther('0'))
      return
    }
    if (depositStatus === 'error') {
      toast({
        position: 'top',
        title: 'Error',
        description: 'Something went wrong. Please try again later.',
      })
      return
    }
  }, [depositStatus, toast])

  useEffect(() => {
    if (redeemStatus === 'success') {
      toast({
        position: 'top',
        title: 'Success',
        status: 'success',
        description: 'Redeemed!',
      })
      setAmount(parseEther('0'))
      return
    }
    if (redeemStatus === 'error') {
      toast({
        position: 'top',
        title: 'Error',
        description: 'Something went wrong. Please try again later.',
      })
      return
    }
  }, [redeemStatus, toast])

  useEffect(() => {
    if (ratio && amount) {
      if (swapDirection) {
        if (!xGGPExchangeRate) {
          setReward(parseEther('0'))
        } else {
          const rewardAmount = BigInt((Number(amount) * Number(ratio)) / Number(WEI_VALUE))
          setReward(rewardAmount)
        }
      } else {
        if (!xGGPExchangeRate) {
          setReward(parseEther('0'))
        } else {
          const rewardAmount = BigInt((Number(amount) / Number(ratio)) * Number(WEI_VALUE))
          setReward(rewardAmount)
        }
      }
    }
  }, [amount, xGGPExchangeRate, swapDirection, ratio])

  useEffect(() => {
    clearAmount()
  }, [currentToken])

  let amountGreaterThanPool = false
  if (maxRedeem) {
    amountGreaterThanPool = amount > maxRedeem
  } else {
    amountGreaterThanPool = amount > 0n
  }

  let amountGreaterThanCap = false
  if (maxDeposit) {
    amountGreaterThanCap = amount > maxDeposit
  } else {
    amountGreaterThanCap = amount > 0n
  }
  const { data: ggpAllowance } = useGGPVaultAllowance(account)

  const allowance = ggpAllowance || 0n

  const displayButton = () => {
    const buttonText = `${swapDirection ? 'Redeem' : 'Deposit'} ${currentToken.displayName}`
    const sufficientBalance = swapDirection
      ? ggAVAXBalance?.value < amount
      : balance?.value < amount

    if (!isConnected) {
      return <ConnectButton />
    }
    if (chain?.id !== avalanche.id && chain?.id !== avalancheFuji.id) {
      return (
        <Button onClick={openChainModal} variant="ink">
          Wrong Network
        </Button>
      )
    }
    if (sufficientBalance) {
      return (
        <Button disabled variant="ink">
          Insufficient Funds
        </Button>
      )
    }
    if (amountGreaterThanCap && !swapDirection) {
      return (
        <Button disabled variant="ink">
          Max deposit amount is {maxDeposit ? displayBN(maxDeposit) : '0.00'} based on vault cap
        </Button>
      )
    }

    if (amountGreaterThanPool && swapDirection) {
      return (
        <Button disabled variant="ink">
          Insufficient Liquidity in Pool
        </Button>
      )
    }

    // console.log('a', amount, isLoading, isRedeemError, isDepositError)
    return (
      <Tooltip
        content="Enter an amount to stake first"
        isDisabled={amount ? true : false}
        placement="top"
      >
        <span className="block w-full">
          <Button
            disabled={
              // TODO Add back in maybe? was casuing a bug
              Boolean(swapDirection && isRedeemError) ||
              Boolean(!swapDirection && isDepositError) ||
              !amount ||
              isLoading
            }
            onClick={
              swapDirection
                ? () => redeem?.(redeemData?.request as any)
                : () => deposit?.(depositData?.request as any)
            }
            variant="ink"
          >
            {isLoading ? 'Loading...' : buttonText}
          </Button>
        </span>
      </Tooltip>
    )
  }

  return (
    <Panel className="seafi-ds animate-fade-in">
      <div className="mb-5 flex items-center justify-between gap-3">
        <h2 style={{ fontSize: 19, fontWeight: 400, letterSpacing: 0 }}>
          Withdraw {currentToken.displayName}
        </h2>
        <Tooltip
          content="GoGoPool is winding down, so this vault no longer accepts deposits. You can redeem your xGGP for GGP whenever the pool has liquidity."
          placement="left"
        >
          <span style={{ color: 'var(--faint)', display: 'inline-flex' }}>
            <InfoCircleIcon />
          </span>
        </Tooltip>
      </div>

      {isWithdrawOnly && (
        <div className="mb-4">
          <GoGoPoolNotice compact />
        </div>
      )}

      <div className="relative">
        <div
          className="mb-4 p-4 md:p-6"
          style={{ background: 'var(--surface-quiet)', borderRadius: 'var(--radius-control)' }}
        >
          {swapDirection ? (
            <StakeForm
              amount={amount}
              balance={ggAVAXBalance?.value || parseEther('0')}
              greaterThanPool={amountGreaterThanPool}
              header="Amount to redeem"
              max={maxRedeem}
              setAmount={setAmount}
              setReward={setReward}
              token={`x${currentToken.displayName}`}
            />
          ) : (
            <StakeForm
              amount={amount}
              balance={balance?.value || 0n}
              greaterThanPool={amountGreaterThanCap}
              header="Amount to deposit"
              max={maxDeposit}
              setAmount={setAmount}
              setReward={setReward}
              token={`${currentToken.displayName}`}
            />
          )}
        </div>
        {!isWithdrawOnly && (
          <div
            className="absolute bottom-[-24px] left-[calc(50%-24px)] flex size-12 cursor-pointer items-center justify-center rounded-full transition-colors"
            onClick={handleSwap}
            style={{ background: 'var(--surface)', border: '1px solid var(--rule)' }}
          >
            <SwapIcon color={currentToken.arrowsColor} size="16px" />
          </div>
        )}
      </div>

      <div
        className="mb-4 p-4 md:p-6"
        style={{ background: 'var(--surface-quiet)', borderRadius: 'var(--radius-control)' }}
      >
        {swapDirection ? (
          <RewardForm
            balance={balance?.value || parseEther('0')}
            reward={reward}
            token={`${currentToken.displayName}`}
          />
        ) : (
          <RewardForm
            balance={ggAVAXBalance?.value || parseEther('0')}
            reward={reward}
            token={`x${currentToken.displayName}`}
          />
        )}
      </div>

      {!isWithdrawOnly && isConnected && (
        <details className="vd-disc">
          <summary data-testid="liquid-staking-accordion-action">
            View vault stats
            <svg
              aria-hidden="true"
              className="vd-chev"
              fill="none"
              height="18"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.6"
              viewBox="0 0 24 24"
              width="18"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </summary>
          <div className="vd-disc-body">
            <Statistics data={statisticData} />
          </div>
        </details>
      )}

      <div className="mt-8 flex flex-col items-center">
        {currentToken.displayName === 'AVAX' ? (
          <ApproveAVAXButton
            amount={amount}
            amountGreaterThanCap={amountGreaterThanCap}
            amountGreaterThanPool={amountGreaterThanPool}
            balance={swapDirection ? ggAVAXBalance?.value || 0n : balance?.value || 0n}
            clearAmount={clearAmount}
            maxDeposit={maxDeposit}
            swapDirection={swapDirection}
          />
        ) : allowance >= amount || swapDirection ? (
          displayButton()
        ) : (
          <ApproveButton
            amount={amount}
            balance={swapDirection ? ggAVAXBalance?.value || 0n : balance?.value || 0n}
            setApproveStatus={() => {
              refetchDeposit()
            }}
          />
        )}
        {isConnected && (
          <>
            {GGPAddress && (
              <div className="mt-4 text-center">
                <button
                  className={`underline underline-offset-2 ${FOCUS}`}
                  onClick={() => {
                    watchAsset({
                      type: 'ERC20',
                      options: {
                        address: GGPAddress,
                        symbol: currentToken.displayName,
                        decimals: 18,
                      },
                    })
                  }}
                  style={{ color: 'var(--muted)', fontSize: 13 }}
                  type="button"
                >
                  Add {currentToken.displayName} token to wallet
                </button>
              </div>
            )}
            <div className="mt-4 text-center">
              <button
                className={`underline underline-offset-2 ${FOCUS}`}
                onClick={() => {
                  watchAsset({
                    type: 'ERC20',
                    options: {
                      address: xGGPAddress,
                      symbol: `x${currentToken.displayName}`,
                      decimals: 18,
                    },
                  })
                }}
                style={{ color: 'var(--muted)', fontSize: 13 }}
                type="button"
              >
                Add x{currentToken.displayName} token to wallet
              </button>
            </div>
          </>
        )}
      </div>
    </Panel>
  )
}
